/**
 * @fileoverview "Request product access" on the profile page: a signed-in
 * person picks products and says why; an administrator decides on the
 * Requests tab. The request is always for the caller, never for someone else.
 */
import { Component, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ApiService } from '../../../core/services/api.service';
import { AccessApiService } from './services/access-api.service';

interface ProductOption {
  pid: number;
  name: string;
}

@Component({
  selector: 'app-request-products',
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <h2 class="mb-1 text-lg font-medium text-slate-800 dark:text-slate-100">Request product access</h2>
      <p class="mb-4 text-sm text-slate-600 dark:text-slate-400">An administrator reviews it and you’ll see the product once it’s approved.</p>
      @if (sent()) {
      <p role="status" class="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200">Your request is with an administrator.</p>
      } @else {
      <fieldset class="mb-4">
        <legend class="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Products</legend>
        <div class="flex flex-wrap gap-2">
          @for (product of products(); track product.pid) {
          <label class="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm dark:border-slate-600">
            <input type="checkbox" [checked]="picked().has(product.pid)" (change)="toggle(product.pid)" class="h-4 w-4 accent-purple-600" />
            <span class="text-slate-800 dark:text-slate-100">{{ product.name }}</span>
          </label>
          }
        </div>
      </fieldset>
      <label for="request-reason" class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Why do you need it?</label>
      <textarea id="request-reason" rows="2" maxlength="1000" [ngModel]="reason()" (ngModelChange)="reason.set($event)"
        class="mb-4 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"></textarea>
      <button type="button" (click)="send()" [disabled]="!ready() || sending()"
        class="min-h-11 rounded-lg bg-purple-600 px-5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-40">
        {{ sending() ? 'Sending…' : 'Send request' }}
      </button>
      }
    </section>
  `,
})
export class RequestProductsComponent implements OnInit {
  private api = inject(ApiService);
  private access = inject(AccessApiService);

  products = signal<ProductOption[]>([]);
  picked = signal<ReadonlySet<number>>(new Set());
  reason = signal('');
  sending = signal(false);
  sent = signal(false);

  ready = computed(() => this.picked().size > 0 && this.reason().trim() !== '');

  ngOnInit(): void {
    this.api.fetchProducts().subscribe((products) =>
      this.products.set(
        products
          .filter((product) => typeof product?.pid === 'number' && typeof product?.name === 'string')
          .map((product) => ({ pid: product.pid, name: product.name })),
      ),
    );
  }

  toggle(pid: number): void {
    const next = new Set(this.picked());
    if (next.has(pid)) next.delete(pid);
    else next.add(pid);
    this.picked.set(next);
  }

  send(): void {
    if (!this.ready() || this.sending()) return;
    this.sending.set(true);
    this.access.requestProducts([...this.picked()], this.reason().trim()).subscribe({
      next: () => {
        this.sending.set(false);
        this.sent.set(true);
      },
      error: () => this.sending.set(false),
    });
  }
}
